// pages/LoginPage.js —— 云平台统一登录（深色主题 + 站点主接线图）
// 登录方式：仅开放账号密码登录；平台不开放自主注册，注册只能通过超级管理员生成的邀请链接进入
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, Modal } from 'antd';
import { GlobalOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { api } from '../services/api';
import StationTopology from '../components/StationTopology';
import i18n from '../i18n';

const PLATFORM_METRICS = [
  { key: 'sites', label: '接入站点', value: '128', unit: '座' },
  { key: 'capacity', label: '装机容量', value: '86.4', unit: 'MWh' },
  { key: 'devices', label: '在线设备', value: '1,296', unit: '台', green: true },
];

// 登录方式：仅账号密码登录（其余登录方式已隐藏）
const ID_LABEL = '账号';
const ID_PLACEHOLDER = '请输入账号';

const LoginPage = () => {
  const navigate = useNavigate();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [rememberPwd, setRememberPwd] = useState(false);
  const [agreed, setAgreed] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [tip, setTip] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // 收到邀请链接的用户在此粘贴链接，走受邀注册流程（链接只能由超级管理员生成）
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteErr, setInviteErr] = useState('');
  // 平台名称（依据手册 p14-p15：个性配置的平台名称需在登录页生效，中文默认为「智慧能源管理云平台」）
  const platformName = localStorage.getItem('platformNameZh') || '智慧能源管理云平台';
  // 个性配置（doc01 3.6）：登录页 logo 随「登录页 logo」保存实时更新
  const loginLogo = localStorage.getItem('themeLoginLogo') || '/logo.png';

  // 回填"记住密码"
  useEffect(() => {
    const savedUser = localStorage.getItem('rememberUser');
    const savedPwd = localStorage.getItem('rememberPwd');
    if (savedUser && savedPwd) {
      setIdentifier(savedUser);
      setPassword(savedPwd);
      setRememberPwd(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setTip('');

    const id = identifier.trim();
    if (!id) { setErrorMsg(`请输入${ID_LABEL}`); return; }
    if (!agreed) { setErrorMsg('请先阅读并同意《用户协议》和《隐私协议》'); return; }

    const pwd = password.trim();
    if (!pwd) { setErrorMsg('请输入密码'); return; }

    setSubmitting(true);
    try {
      const result = await api.login({ username: id, password: pwd });
      if (result.success) {
        localStorage.setItem('currentUser', JSON.stringify(result.user));
        localStorage.setItem('sessionToken', result.token);
        if (result.clientIp) localStorage.setItem('loginIp', result.clientIp);
        if (rememberPwd) {
          localStorage.setItem('rememberUser', id);
          localStorage.setItem('rememberPwd', pwd);
        } else {
          localStorage.removeItem('rememberUser');
          localStorage.removeItem('rememberPwd');
        }
        navigate('/portal');
      } else {
        setErrorMsg(result.message || '账号或密码错误');
      }
    } catch (err) {
      setErrorMsg('请求异常，请检查后端服务是否已启动');
    } finally {
      setSubmitting(false);
    }
  };

  const goInvite = () => {
    const raw = inviteLink.trim();
    const m = raw.match(/(?:code=|referral\/)([A-Za-z0-9]{6,32})/);
    const code = m ? m[1] : (/^[A-Za-z0-9]{6,32}$/.test(raw) ? raw : '');
    if (!code) { setInviteErr('链接格式不正确，请粘贴完整的邀请链接'); return; }
    setInviteOpen(false);
    navigate(`/join-company?code=${code}`);
  };

  return (
    <div className="login-page">
      <div className="lg-lang">
        <Select
          value={i18n.language}
          onChange={(lang) => i18n.changeLanguage(lang)}
          style={{ width: 118 }}
          size="middle"
          suffixIcon={<GlobalOutlined />}
          options={[
            { value: 'zh', label: '简体中文' },
            { value: 'en', label: 'English' },
            { value: 'th', label: 'ภาษาไทย' },
          ]}
        />
      </div>

      {/* 左：品牌 + 能量流转可视化 */}
      <div className="lg-brand">
        <img className="lg-logo" src={loginLogo} alt="能源管家" />
        <h1 className="lg-title">能源管家<em>{platformName}</em></h1>
        <p className="lg-subtitle">GSE ENERGY MANAGEMENT CLOUD PLATFORM</p>
        <p className="lg-tagline">打通光伏、储能与负荷的本地协同，实时感知每一度电的流向。</p>

        <div className="lv-flow">
          <div className="lv-flow-head">
            <span className="lv-flow-title">主接线图</span>
            <span className="lv-status"><i className="lv-dot" />运行正常</span>
          </div>

          <StationTopology />

          <div className="lv-metrics">
            {PLATFORM_METRICS.map((m) => (
              <div className="lv-metric" key={m.key}>
                <div className="k">{m.label}</div>
                <div className={`v${m.green ? ' green' : ''}`}>{m.value}<span>{m.unit}</span></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 右：登录卡片 */}
      <div className="lg-card">
        <form className="lg-form" onSubmit={handleLogin}>
          <h2 className="lg-heading">登录</h2>
          <p className="lg-heading-sub">请输入账号和密码登录</p>

          <div className="lg-field">
            <label className="lg-label" htmlFor="lg-id"><span className="req">*</span>{ID_LABEL}</label>
            <input
              id="lg-id"
              className="lg-input"
              placeholder={ID_PLACEHOLDER}
              autoComplete="username"
              value={identifier}
              onChange={(e) => { setIdentifier(e.target.value); setErrorMsg(''); }}
            />
          </div>

          <div className="lg-field">
            <label className="lg-label" htmlFor="lg-password"><span className="req">*</span>密码</label>
            <div className="lg-pwd-wrap">
              <input
                id="lg-password"
                className="lg-input"
                type={showPwd ? 'text' : 'password'}
                placeholder="请输入密码"
                autoComplete="current-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setErrorMsg(''); }}
              />
              <span className="lg-eye" onClick={() => setShowPwd((v) => !v)}>
                {showPwd ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              </span>
            </div>
          </div>

          <div className="lg-links">
            <span />
            <button
              type="button"
              className="lg-link"
              onClick={() => navigate('/reset-password')}
            >
              忘记密码？
            </button>
          </div>

          <div className="lg-checks">
            <label className="lg-check">
              <input type="checkbox" checked={rememberPwd} onChange={(e) => setRememberPwd(e.target.checked)} />
              记住密码
            </label>
            <label className="lg-check">
              <input type="checkbox" checked={agreed} onChange={(e) => { setAgreed(e.target.checked); setErrorMsg(''); }} />
              我已阅读并同意<a href="#/agreement" onClick={(e) => e.preventDefault()}>《用户协议》</a>与<a href="#/privacy" onClick={(e) => e.preventDefault()}>《隐私协议》</a>
            </label>
          </div>

          <div className="lg-error">{errorMsg || (tip ? <span className="lg-tip">{tip}</span> : '')}</div>

          <button type="submit" className="lg-submit" disabled={submitting}>
            {submitting ? '登录中' : '登 录'}
          </button>

          <button
            type="button"
            className="lg-link lg-demo-link"
            onClick={() => { setInviteErr(''); setInviteLink(''); setInviteOpen(true); }}
          >
            收到邀请链接？注册加入公司
          </button>

          <div className="lg-no-register">平台不开放自主注册，如需开通账号请联系超级管理员获取邀请链接。</div>
        </form>

        {/* 受邀注册：粘贴超级管理员生成的邀请链接后进入注册流程 */}
        <Modal
          open={inviteOpen}
          title="邀请注册"
          okText="下一步"
          cancelText="取消"
          onOk={goInvite}
          onCancel={() => setInviteOpen(false)}
          width={460}
          destroyOnHidden
        >
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', marginBottom: 8 }}>
            请粘贴管理员发送给您的邀请链接（形如 /referral/xxxxxxxx）
          </div>
          <input
            className="lg-input"
            style={{ width: '100%' }}
            placeholder="请粘贴邀请链接"
            value={inviteLink}
            onChange={(e) => { setInviteLink(e.target.value); setInviteErr(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') goInvite(); }}
          />
          {inviteErr ? <div className="lg-error" style={{ marginTop: 8 }}>{inviteErr}</div> : null}
        </Modal>
      </div>

      <div className="lg-footer">© 2026 杭州吉晟智慧能源有限公司 · gsecloud.cn</div>
    </div>
  );
};

export default LoginPage;